import { creating, exist } from '@campaign-portal/namespace/common/id';
import {
	MessageTemplate,
	SmsTemplateSettings,
	ViberTemplateSettings,
	WhatsappTemplateSettings,
} from '@campaign-portal/namespace/entities/templates/specs';
import { IdObject } from '@campaign-portal/namespace/common/idObject';
import { NamedObject } from '@campaign-portal/namespace/common/namedObject';
import {
	PreModerationDeclineType,
	PreModerationEventStatus,
	TrafficType,
} from '@campaign-portal/namespace/common/enums';
import { TagMode } from '@campaign-portal/components-library';

export interface MessageTemplateEntityConfig<ID = exist | creating> extends Partial<MessageTemplate<ID>> {
	id: ID;
	name: string;
	status: PreModerationEventStatus;
}

export class MessageTemplateEntity<ID = exist | creating> implements MessageTemplate<ID> {
	id: ID;
	name: string;
	ownerId?: exist;
	sender?: IdObject<exist> & NamedObject;
	messageTemplate?: string;
	smsSettings?: SmsTemplateSettings;
	viberSettings?: ViberTemplateSettings;
	whatsappSettings?: WhatsappTemplateSettings;
	channelType?: TrafficType;
	isFlashed?: boolean;
	scheduledDate?: string;
	declineType?: PreModerationDeclineType;
	declineReason?: string;
	status: PreModerationEventStatus;
	creationDate?: string | null;
	createdBy?: string | null;
	lastUpdated?: string | null;
	modifiedBy?: string | null;

	constructor(config: MessageTemplateEntityConfig<ID>) {
		this.id = config.id;
		this.name = config.name;
		this.status = config.status;
		this.ownerId = config.ownerId;
		this.sender = config.sender;
		this.messageTemplate = config.messageTemplate;
		this.smsSettings = config.smsSettings;
		this.viberSettings = config.viberSettings;
		this.whatsappSettings = config.whatsappSettings;
		this.channelType = config.channelType;
		this.isFlashed = config.isFlashed;
		this.scheduledDate = config.scheduledDate;
		this.declineType = config.declineType;
		this.declineReason = config.declineReason;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}

	isNew(): boolean {
		return this.status === PreModerationEventStatus.NEW;
	}

	isRepeat(): boolean {
		return this.status === PreModerationEventStatus.REPEAT;
	}

	isInProgress(): boolean {
		return this.status === PreModerationEventStatus.IN_PROGRESS;
	}

	isDeclined(): boolean {
		return this.status === PreModerationEventStatus.DECLINED;
	}

	isConfirmed(): boolean {
		return this.status === PreModerationEventStatus.CONFIRMED;
	}

	isCancelled(): boolean {
		return this.status === PreModerationEventStatus.CANCELLED;
	}

	isPending(): boolean {
		return this.isNew() || this.isRepeat() || this.isInProgress();
	}

	isRejected(): boolean {
		return this.isDeclined() || this.isCancelled();
	}

	canCancel(): boolean {
		return this.isPending();
	}

	canRepeat(): boolean {
		return this.isRejected();
	}

	canCreateCampaign(isPreModeratedChannel: boolean, hasActiveSender: boolean): boolean {
		if (isPreModeratedChannel) {
			return this.isConfirmed() && hasActiveSender;
		}
		return true;
	}

	getStatusColor(): TagMode {
		switch (this.status) {
			case PreModerationEventStatus.NEW:
			case PreModerationEventStatus.REPEAT:
				return 'warning';
			case PreModerationEventStatus.IN_PROGRESS:
				return 'info';
			case PreModerationEventStatus.DECLINED:
				return 'error';
			case PreModerationEventStatus.CONFIRMED:
				return 'success';
			case PreModerationEventStatus.CANCELLED:
				return 'gray-light';
		}
	}
}
